import {
  FileText, Download, Package, BookOpen, FolderArchive,
  Video, Network, Wifi, Bluetooth, Cpu, Volume2, HardDrive,
} from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { useSite } from "@/context/SiteContext";
import { resolveMedia } from "@/lib/media";

const CAT_ICONS = {
  Video: Video,
  LAN: Network,
  WiFi: Wifi,
  Wifi: Wifi,
  Bluetooth: Bluetooth,
  BIOS: Cpu,
  Chipset: Cpu,
  Audio: Volume2,
  Drivers: HardDrive,
  "Fichas técnicas": FileText,
  Manuales: BookOpen,
  Catálogo: Package,
};

function groupByCategory(items) {
  const groups = [];
  const index = {};
  items.forEach((it) => {
    const cat = it.category || "Otros";
    if (index[cat] === undefined) {
      index[cat] = groups.length;
      groups.push({ category: cat, items: [] });
    }
    groups[index[cat]].items.push(it);
  });
  return groups;
}

export default function DownloadsSection() {
  const { content, downloads } = useSite();
  const d = content?.downloads_section || {};
  const groups = groupByCategory(downloads);

  return (
    <section id="descargas" className="relative border-y border-white/10 bg-[color:var(--aurex-surface-2)]">
      <div className="mx-auto max-w-[1400px] px-6 py-24 md:px-10 md:py-32">
        <Reveal className="mb-14 max-w-2xl">
          <span className="font-mono-jb text-xs uppercase tracking-[0.35em] text-[color:var(--aurex-accent-soft)]">
            {d.overline || "Descargas"}
          </span>
          <h2 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-[color:var(--aurex-text)] sm:text-5xl">
            {d.title}
          </h2>
          <p className="mt-4 text-[color:var(--aurex-muted)]">{d.subtitle}</p>
        </Reveal>

        <div className="space-y-14">
          {groups.map((group) => {
            const GIcon = CAT_ICONS[group.category] || FileText;
            return (
              <div key={group.category} data-testid={`download-group-${group.category}`}>
                <Reveal className="mb-6 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl copper-bg text-[color:var(--aurex-on-accent)]">
                    <GIcon size={18} />
                  </span>
                  <h3 className="font-display text-2xl font-bold text-[color:var(--aurex-text)]">{group.category}</h3>
                  <span className="font-mono-jb text-[11px] text-zinc-600">{group.items.length}</span>
                </Reveal>

                <Stagger className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {group.items.map((item) => {
                    const Icon = CAT_ICONS[item.category] || FileText;
                    const available = item.file_url && item.file_url.length > 0;
                    const href = available ? resolveMedia(item.file_url) : null;
                    return (
                      <StaggerItem key={item.id}>
                        <div
                          className="group flex items-center gap-5 rounded-2xl border border-white/10 bg-[color:var(--aurex-surface)] p-5 transition-colors duration-300 hover:border-white/25"
                          data-testid={`download-item-${item.id}`}
                        >
                          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-[color:var(--aurex-accent-soft)]">
                            <Icon size={22} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <h4 className="font-display text-lg font-bold text-[color:var(--aurex-text)]">{item.title}</h4>
                              <span className="rounded-full border border-white/10 px-2 py-0.5 font-mono-jb text-[10px] uppercase tracking-[0.12em] text-[color:var(--aurex-muted)]">
                                {item.file_type}
                              </span>
                            </div>
                            {item.description && <p className="mt-0.5 truncate text-sm text-[color:var(--aurex-muted)]">{item.description}</p>}
                            <div className="mt-1 font-mono-jb text-[11px] text-zinc-600">
                              {[item.category, item.version, item.size].filter(Boolean).join(" · ")}
                            </div>
                          </div>
                          {available ? (
                            <a
                              data-testid={`download-btn-${item.id}`}
                              href={href}
                              target="_blank"
                              rel="noreferrer"
                              download
                              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full copper-bg text-[color:var(--aurex-on-accent)] transition-transform duration-300 hover:scale-110"
                            >
                              <Download size={18} />
                            </a>
                          ) : (
                            <span className="shrink-0 rounded-full border border-white/10 px-3 py-1.5 font-mono-jb text-[10px] uppercase tracking-[0.14em] text-zinc-600">
                              {d.pending_label || "Pronto"}
                            </span>
                          )}
                        </div>
                      </StaggerItem>
                    );
                  })}
                </Stagger>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
